/**
 * Data layer for `applovin-integration`. Thin typed fetch wrappers over /api/v1/applovin-integration.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ApplovinIntegrationRow {
	id: number;
	app_key: string;
	bundle_id: string;
	platform: string;
	sdk_version: string;
	config: Record<string, unknown>;
	enabled: boolean;
	created_at: number;
	updated_at: number;
}

export interface ListApplovinIntegrationResponse {
	items: ApplovinIntegrationRow[];
}

export interface CreateApplovinIntegrationBody {
	app_key: string;
	bundle_id: string;
	platform: string;
	sdk_version?: string;
	config?: Record<string, unknown>;
	enabled?: boolean;
}

export interface CreateApplovinIntegrationResponse {
	ok: boolean;
	app_key: string;
	created_at: number;
}

const BASE = "/api/v1/applovin-integration";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListApplovinIntegrationResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListApplovinIntegrationResponse>(res);
}

export async function create(body: CreateApplovinIntegrationBody): Promise<CreateApplovinIntegrationResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateApplovinIntegrationResponse>(res);
}

export async function remove(id: string): Promise<{ ok: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: boolean }>(res);
}

export const applovinIntegrationQueryKeys = {
	all: ["applovin-integration"] as const,
	list: () => [...applovinIntegrationQueryKeys.all] as const,
	detail: (id: string) => [...applovinIntegrationQueryKeys.all, id] as const,
};
